import { apiClient } from './client';
import type { Product, Category } from '../types';

interface GetProductsParams {
  category_id?: number;
  available?: boolean;
}

interface ProductsResponse {
  products: Product[];
}

interface CategoryWithProducts extends Category {
  products: Product[];
}

interface AdminCatalogResponse {
  categories: CategoryWithProducts[];
}

interface CategoriesResponse {
  categories: Category[];
}

interface CreateProductData {
  name: string;
  description: string;
  category_id: number;
  price: number;
  weight: number | null;
  pieces?: number | null;
  calories?: number | null;
  protein?: number | null;
  fat?: number | null;
  carbohydrates?: number | null;
  is_available: boolean;
  image_url?: string;
}

interface UploadImageResponse {
  image_url: string;
}

export const adminProductsApi = {
  // Get all products for admin (includes unavailable)
  async getAdminCatalog(): Promise<AdminCatalogResponse> {
    const response = await apiClient.get<AdminCatalogResponse>('/admin/catalog');
    return response;
  },

  // Get all products (with optional filtering)
  async getProducts(params?: GetProductsParams): Promise<ProductsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.category_id) {
      queryParams.append('category_id', params.category_id.toString());
    }
    if (params?.available !== undefined) {
      queryParams.append('available', params.available.toString());
    }

    const queryString = queryParams.toString();
    const url = `/products${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<ProductsResponse>(url);
    return response;
  },

  // Get single product
  async getProduct(id: number): Promise<Product> {
    const response = await apiClient.get<Product>(`/products/${id}`);
    return response;
  },

  // Get all categories
  async getCategories(): Promise<CategoriesResponse> {
    const response = await apiClient.get<CategoriesResponse>('/categories');
    return response;
  },

  // Create product
  async createProduct(data: CreateProductData): Promise<Product> {
    const response = await apiClient.post<Product>('/admin/products', data);
    return response;
  },

  // Update product
  async updateProduct(id: number, data: Partial<CreateProductData>): Promise<Product> {
    const response = await apiClient.put<Product>(`/admin/products/${id}`, data);
    return response;
  },

  // Delete product
  async deleteProduct(id: number): Promise<void> {
    await apiClient.delete(`/admin/products/${id}`);
  },

  // Upload image
  async uploadImage(productId: number, file: File): Promise<UploadImageResponse> {
    const formData = new FormData();
    formData.append('product_id', productId.toString());
    formData.append('image', file);

    const response = await apiClient.upload<UploadImageResponse>(
      '/admin/products/upload-image',
      formData
    );
    return response;
  },

  // Add image to gallery
  async addProductImage(
    productId: number,
    imageUrl: string,
    sortOrder: number,
    isPrimary: boolean
  ): Promise<void> {
    await apiClient.post(`/admin/products/${productId}/images`, {
      image_url: imageUrl,
      sort_order: sortOrder,
      is_primary: isPrimary,
    });
  },

  // Delete image from gallery
  async deleteProductImage(productId: number, imageId: number): Promise<void> {
    await apiClient.delete(`/admin/products/${productId}/images/${imageId}`);
  },

  // Set image as primary
  async setPrimaryImage(productId: number, imageId: number): Promise<void> {
    await apiClient.patch(`/admin/products/${productId}/images/${imageId}/primary`, {});
  },

  // Reorder product
  async reorderProduct(id: number, direction: 'up' | 'down'): Promise<Product[]> {
    const response = await apiClient.patch<Product[]>(`/admin/products/${id}/reorder`, { direction });
    return response;
  },
};
